import { Button, Checkbox } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import React, { forwardRef, Fragment, useRef } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import getSetting, { getLogoUrl } from "../../api/getSettings";
import number2words from "../../utils/numberToWords";
import bookLogo from "./../../assets/images/bookLogo.png";
import "./style.css";
import camelize from "../../utils/toCamelCase";

// Function to format number with Indian numbering system (commas)
// Indian system: first comma after 3 digits from right, then every 2 digits
// Examples: 1,000 | 10,000 | 1,00,000 | 10,00,000 | 1,00,00,000
const formatIndianNumber = (num) => {
  const numStr = num.toFixed(2);
  const parts = numStr.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] || '';

  // Add commas according to Indian numbering system
  let formatted = '';
  const reversed = integerPart.split('').reverse();

  for (let i = 0; i < reversed.length; i++) {
    // Add comma after first 3 digits (i === 3)
    // Then add comma every 2 digits after that (i > 3 and (i - 3) % 2 === 0)
    if (i === 3) {
      formatted = ',' + formatted;
    } else if (i > 3 && (i - 3) % 2 === 0) {
      formatted = ',' + formatted;
    }
    formatted = reversed[i] + formatted;
  }

  return formatted + (decimalPart ? '.' + decimalPart : '');
};

const PrintToPdf = forwardRef(({ data, invoiceData, documentType = "sale", sortAlphabetically = false }, ref) => {
  // Calculate totals for display
  const totalQuantity = data?.saleInvoiceProduct?.reduce((sum, item) => sum + (item.product_quantity || 0), 0) || 0;
  const grossTotal = data?.saleInvoiceProduct?.reduce((sum, item) => {
    return sum + (item.product_quantity * item.product_sale_price * item.product_sale_conversion);
  }, 0) || 0;
  const totalProductDiscountAmount = data?.saleInvoiceProduct?.reduce((sum, item) => {
    const itemGross = item.product_quantity * item.product_sale_price * item.product_sale_conversion;
    const itemDiscount = (itemGross * (item.product_sale_discount || 0)) / 100;
    return sum + itemDiscount;
  }, 0) || 0;
  const subtotalAfterProductDiscount = grossTotal - totalProductDiscountAmount;
  const additionalDiscount = data?.discount || 0;
  const roundOffAmount = data?.round_off_amount || 0;
  const finalTotal = subtotalAfterProductDiscount - additionalDiscount + roundOffAmount;
  const paidAmount = data?.paid_amount || 0;
  const dueAmount = data?.due_amount || 0;

  // Extract unique currencies and their conversion rates used in the invoice
  const getCurrencyRates = () => {
    if (!data?.saleInvoiceProduct) return [];
    const currencyMap = new Map();

    data.saleInvoiceProduct.forEach(item => {
      const currency = item.product_sale_currency || '₹';
      const conversion = item.product_sale_conversion || 1;

      if (!currencyMap.has(currency)) {
        currencyMap.set(currency, conversion);
      }
    });

    return Array.from(currencyMap.entries()).map(([symbol, rate]) => ({
      symbol,
      rate
    }));
  };

  const currencyRates = getCurrencyRates();

  // Get document title based on type
  const getDocumentTitle = () => {
    switch (documentType) {
      case "quotation": return "QUOTATION";
      case "challan": return "DELIVERY CHALLAN";
      default: return "SALE INVOICE";
    }
  };

  // Get "Bill To" label based on document type
  const getBillToLabel = () => {
    switch (documentType) {
      case "quotation": return "Quotation To";
      case "challan": return "Challan To";
      default: return "Bill To";
    }
  };

  // Get document number label based on type
  const getDocumentNumberLabel = () => {
    switch (documentType) {
      case "quotation": return "Quotation No";
      case "challan": return "Challan No";
      default: return "Invoice No";
    }
  };

  // Get document date label based on type
  const getDocumentDateLabel = () => {
    switch (documentType) {
      case "quotation": return "Quotation Date";
      case "challan": return "Challan Date";
      default: return "Invoice Date";
    }
  };

  const getDetailsSectionTitle = () => {
    switch (documentType) {
      case "quotation": return "Quotation Details";
      case "challan": return "Challan Details";
      default: return "Invoice Details";
    }
  };

  // Get document-specific terms and conditions from settings
  const getDocumentTerms = () => {
    if (!invoiceData) return null;
    console.log("Getting terms for documentType:", documentType);
    console.log("Available fields:", {
      quotation_terms: invoiceData.quotation_terms,
      challan_terms: invoiceData.challan_terms,
      sale_invoice_terms: invoiceData.sale_invoice_terms,
      terms: invoiceData.terms
    });
    switch (documentType) {
      case "quotation":
        return invoiceData.quotation_terms || invoiceData.terms || null;
      case "challan":
        return invoiceData.challan_terms || invoiceData.terms || null;
      default:
        return invoiceData.sale_invoice_terms || invoiceData.terms || null;
    }
  };

  return (
    <Fragment>
      <div
        ref={ref}
        className="wrapper"
        data-invoice-number={data?.prefix + data?.invoice_number}
        data-invoice-date={moment(data?.date).format("DD-MMM-YYYY")}
        // Reserve space so absolute bottom footer never overlaps signatures/content
        style={{ position: 'relative', paddingBottom: '110px' }}
      >
        <div className="invoice-top-meta">
          <span className="invoice-top-meta-left">
            {getDocumentNumberLabel()}: {data?.prefix + data?.invoice_number}
          </span>
          <span className="invoice-top-meta-right">
            {getDocumentDateLabel()}: {moment(data?.date).format("DD-MMM-YYYY")}
          </span>
        </div>
        {/* Company Detail - Modern Compact Header */}
        <div style={{
          gridColumn: '1/4',
          gridRow: '1',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '15px 20px',
          borderBottom: '2px solid #003a8c',
          marginBottom: '15px'
        }}>
          {/* Left Side - Logo */}
          <div style={{ flex: '0 0 auto', marginRight: '20px' }}>
            <img src={getLogoUrl(invoiceData?.logo) || bookLogo} style={{ maxHeight: '80px', width: 'auto' }} alt="Company Logo" />
          </div>

          {/* Center - Company Info */}
          <div style={{ flex: '1', textAlign: 'center', padding: '0 20px' }}>
            <h2 style={{
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1a1a1a',
              letterSpacing: '1px'
            }}>
              {invoiceData?.company_name}
            </h2>
            <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.6' }}>
              <div style={{ marginBottom: '4px' }}>{invoiceData?.address}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
                {invoiceData?.phone && <span>📞 {invoiceData.phone}</span>}
                {invoiceData?.email && <span>✉️ {invoiceData.email}</span>}
              </div>
            </div>
          </div>

          {/* Right Side - Invoice Title */}
          <div style={{
            flex: '0 0 auto',
            textAlign: 'right',
            borderLeft: '2px solid #e8e8e8',
            paddingLeft: '20px',
            minWidth: '180px'
          }}>
            <div style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#003a8c',
              marginBottom: '8px',
              letterSpacing: '1px'
            }}>
              {getDocumentTitle()}
            </div>
            {invoiceData?.tag_line && (
              <div style={{
                fontSize: '10px',
                // color: '#999',
                fontStyle: 'italic',
                marginTop: '4px'
              }}>
                {invoiceData.tag_line}
              </div>
            )}
          </div>
        </div>

        {/* GST Exemption Note - Compact */}
        {invoiceData?.gst_exemption_note && (
          <div style={{
            gridColumn: '1/4',
            gridRow: '2',
            textAlign: 'center',
            padding: '8px 0',
            fontSize: '11px',
            color: '#666',
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #e8e8e8',
            borderBottom: '1px solid #e8e8e8',
            marginBottom: '15px'
          }}>
            {invoiceData.gst_exemption_note}
          </div>
        )}

        {/* Customer Detail and Invoice Detail - Two Card Layout */}
        <div style={{
          gridColumn: '1/4',
          gridRow: invoiceData?.gst_exemption_note ? '3' : '2',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '12px',
          marginTop: '5px'
        }}>
          {/* Customer Detail Card */}
          <div style={{
            flex: '1',
            border: '2px solid #000',
            borderRadius: '4px',
            padding: '8px',
            backgroundColor: '#fafafa',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
          }}>
            <h4 style={{
              margin: '0 0 6px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              borderBottom: '2px solid #003a8c',
              paddingBottom: '4px',
              color: '#003a8c'
            }}>
              {getBillToLabel()}
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <th style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontWeight: '600',
                    width: '30%',
                    fontSize: '16px',
                    color: '#666'
                  }}>
                    Name:
                  </th>
                  <td style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    {data?.customer.name}
                  </td>
                </tr>
                <tr>
                <th
  style={{
    padding: '2px 0',
    textAlign: 'left',
    fontWeight: '600',
    width: '30%',
    fontSize: '16px',
    color: '#666',
    verticalAlign: 'top'   // ✅ important
  }}
>
  Address:
</th>

<td
  style={{
    padding: '2px 0',
    textAlign: 'left',
    fontSize: '16px',
    fontWeight: '500',
    verticalAlign: 'top',   // ✅ important
    wordBreak: 'break-word',
    whiteSpace: 'normal'
  }}
>
  {data?.customer.address || 'N/A'}
</td>
                </tr>
                <tr>
                  <th style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontWeight: '600',
                    width: '30%',
                    fontSize: '16px',
                    color: '#666'
                  }}>
                    Contact:
                  </th>
                  <td style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    {data?.customer.phone || 'N/A'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Invoice Detail Card */}
          <div style={{
            flex: '1',
            border: '2px solid #000',
            borderRadius: '4px',
            padding: '8px',
            backgroundColor: '#fafafa',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
          }}>
            <h4 style={{
              margin: '0 0 6px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              borderBottom: '2px solid #003a8c',
              paddingBottom: '4px',
              color: '#003a8c'
            }}>
              {getDetailsSectionTitle()}
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <th style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontWeight: '600',
                    width: '35%',
                    fontSize: '16px',
                    color: '#666'
                  }}>
                    {getDocumentNumberLabel()}:
                  </th>
                  <td style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    {data?.prefix + data?.invoice_number}
                  </td>
                </tr>
                <tr>
                  <th style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontWeight: '600',
                    width: '35%',
                    fontSize: '16px',
                    color: '#666'
                  }}>
                    {getDocumentDateLabel()}:
                  </th>
                  <td style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    {moment(data?.date).format("DD-MMM-YYYY")}
                  </td>
                </tr>
                <tr>
                  <th style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontWeight: '600',
                    width: '35%',
                    fontSize: '16px',
                    color: '#666'
                  }}>
                    Order No:
                  </th>
                  <td style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    {data?.invoice_order_number || 'N/A'}
                  </td>
                </tr>
                <tr>
                  <th style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontWeight: '600',
                    width: '35%',
                    fontSize: '16px',
                    color: '#666'
                  }}>
                    Order Date:
                  </th>
                  <td style={{
                    padding: '2px 0',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    {data?.invoice_order_date
                      ? moment(data.invoice_order_date).format("DD-MMM-YYYY")
                      : 'N/A'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Product details */}
        <div style={{
          gridColumn: '1/4',
          gridRow: invoiceData?.gst_exemption_note ? '4' : '3',
          justifyContent: 'stretch',
          fontSize: '14px',
          padding: '0.6em 0.6em 0 0.6em',
          marginBottom: '0'
        }}>
          <table className="table1" style={{ marginBottom: '0' }}>
            <thead>
              <th>Sl</th>
              <th>ISBN</th>
              <th>Title</th>
              <th>Author</th>
              <th>Publisher</th>
              <th>Price</th>
              <th>Qty</th>
              <th>Gross</th>
              <th>Dis%</th>
              <th>Amount</th>
            </thead>
            <tbody>
              {data &&
                (sortAlphabetically
                  ? [...data.saleInvoiceProduct].sort((a, b) => {
                    const nameA = (a.product?.name || '').toLowerCase();
                    const nameB = (b.product?.name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  : data.saleInvoiceProduct
                ).map((d, index) => {
                  const itemGross = d.product_quantity * d.product_sale_price * d.product_sale_conversion;
                  const itemDiscountAmount = (itemGross * (d.product_sale_discount || 0)) / 100;
                  const itemAmount = itemGross - itemDiscountAmount;

                  return (
                    <tr key={d.id}>
                      <td><p style={{ margin: 0 }}>{index + 1}</p></td>
                      <td><p style={{ margin: 0 }}>{d.product?.isbn}</p></td>
                      <td><p style={{ margin: 0 }}>{d.product?.name}</p></td>
                      {/* <td><p style={{ margin: 0 }}>{d.product?.author}</p></td> */}
                      <td style={{ maxWidth: '70px' }}>
  <p style={{ margin: 0, wordWrap: 'break-word', whiteSpace: 'normal' }}>
    {d.product?.author}
  </p>
</td>
                      {/* <td><p style={{ margin: 0 }}>{d.product?.book_publisher?.name || 'N/A'}</p></td> */}
                      <td style={{ maxWidth: '100px' }}>
  <p style={{ margin: 0, wordWrap: 'break-word', whiteSpace: 'normal' }}>
    {d.product?.book_publisher?.name || "—"}
  </p>
</td>
                      <td><p style={{ margin: 0 }}>{(d.product_sale_currency === 'Rs.' ? '₹' : d.product_sale_currency) || '₹'}{d.product_sale_price}</p></td>
                      <td><p style={{ margin: 0 }}>{d.product_quantity}</p></td>
                      <td style={{ whiteSpace: 'nowrap' }}><p style={{ margin: 0 }}>₹{itemGross.toFixed(2)}</p></td>
                      <td><p style={{ margin: 0 }}>{d.product_sale_discount || 0}%</p></td>
                      <td style={{ whiteSpace: 'nowrap' }}><p style={{ margin: 0 }}>₹{itemAmount.toFixed(2)}</p></td>
                    </tr>
                  );
                })}
              {/* Summary rows in the table */}
              <tr style={{ borderTop: '1px solid silver', fontWeight: 'bold' }}>
                <td colSpan="6" style={{ textAlign: 'right' }}>Total</td>
                <td>{totalQuantity}</td>
                <td style={{ whiteSpace: 'nowrap' }}>₹{grossTotal.toFixed(2)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>₹{totalProductDiscountAmount.toFixed(2)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>₹{subtotalAfterProductDiscount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Summary Section - Two Column Layout */}
        <div style={{
          gridColumn: '1/4',
          gridRow: '5',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '20px',
          marginTop: '-60px',
          marginBottom: '0',
          paddingTop: '0',
          padding: '0 0.6em 0.6em 0.6em'
        }}>
          {/* Left Column - Currency Conversion Rates */}
          <div style={{ width: '10%', alignSelf: 'flex-start', marginTop: '0px', marginBottom: '0' }}>
            {currencyRates.length > 0 && (
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '1px',
                backgroundColor: '#f9f9f9'
              }}>

                <table style={{ width: '100%', fontSize: '14px' }}>

                  <tbody>
                    {currencyRates.map((item, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '1px', textAlign: 'left' }}>{item.symbol}1 :</td>
                        <td style={{ padding: '1px', textAlign: 'left', fontWeight: '500' }}>
                          ₹{item.rate.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Right Column - Grand Total */}
          <div style={{ width: '30%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <table className="table2" style={{ width: '100%' }}>
              <tbody>
                {additionalDiscount > 0 && (
                  <tr style={{ borderTop: '1px solid #ddd' }}>
                    <th style={{ padding: '8px', textAlign: 'right', width: '70%' }}>
                      Additional Discount (-)
                    </th>
                    <td style={{ padding: '8px', fontWeight: 'bold', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      ₹{formatIndianNumber(additionalDiscount)}
                    </td>
                  </tr>
                )}
                {roundOffAmount !== 0 && (
                  <tr style={{ borderTop: '1px solid #ddd' }}>
                    <th style={{ padding: '8px', textAlign: 'right', width: '70%' }}>
                      Round Off {roundOffAmount > 0 ? '(+)' : '(-)'}
                    </th>
                    <td style={{ padding: '8px', fontWeight: 'bold', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      ₹{formatIndianNumber(Math.abs(roundOffAmount))}
                    </td>
                  </tr>
                )}
                <tr style={{ borderTop: '2px solid #000', fontWeight: 'bold' }}>
                  <th style={{ padding: '10px', textAlign: 'right', fontSize: '14px' }}>Grand Total</th>
                  <td style={{ padding: '10px', fontWeight: 'bold', textAlign: 'right', fontSize: '16px', whiteSpace: 'nowrap' }}>
                    ₹{formatIndianNumber(finalTotal)}
                  </td>
                </tr>
                {paidAmount > 0 && (
                  <>
                    <tr>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Paid Amount</th>
                      <td style={{ padding: '8px', textAlign: 'right', color: 'green', whiteSpace: 'nowrap' }}>
                        ₹{formatIndianNumber(paidAmount)}
                      </td>
                    </tr>
                    <tr style={{ borderTop: '1px solid #ddd' }}>
                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>Due Amount</th>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: 'red', whiteSpace: 'nowrap' }}>
                        ₹{formatIndianNumber(dueAmount)}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Amount in Words and Footer Section - Combined in Grid */}
        <div style={{
          gridColumn: '1/4',
          gridRow: '6',
          display: 'flex',
          flexDirection: 'column',
          padding: '0 0.6em 0 0.6em',
          marginTop: '-70px',
          marginBottom: '0',
          paddingBottom: '5px'
        }}>
          {/* Amount in Words */}
          <div style={{
            textAlign: 'left',
            padding: '0',
            marginBottom: '20px'
          }}>
            <p style={{ margin: '0', fontSize: '14px', lineHeight: '1.4' }}>
              <b>Amount In Words: </b>
              Rs.{" "}
              {camelize(
                number2words(
                  Math.round(finalTotal)
                )
              )}{" "}
              Only.
            </p>
          </div>

          {/* Footer Section - Two Columns */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '0',
            marginTop: '0',
            width: '100%'
          }}>
            {/* Top Row - Terms & Conditions and Pay To */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              flexWrap: 'nowrap',
              marginBottom: '5px'
            }}>
              {/* Left Column - Terms & Conditions */}
              <div style={{
                width: '48%',
                padding: '0 10px 0 0',
                textAlign: 'left',
                flexShrink: 0
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>
                  <b>Terms & Conditions: </b>
                </p>
                <div style={{ fontSize: '11px', marginBottom: '0' }}>
                  {(() => {
                    const terms = getDocumentTerms();
                    console.log("Terms to display:", terms);
                    if (!terms || terms.trim() === '') return 'Standard terms and conditions apply.';
                    // Try splitting by \r\n first, then \n, then \r
                    const lines = terms.split(/\r\n|\n|\r/).filter(line => line.trim() !== '');
                    if (lines.length === 0) return terms; // If no splits, show as-is
                    return lines.map((item, key) => (
                      <Fragment key={key}>
                        {item}
                        <br />
                      </Fragment>
                    ));
                  })()}
                </div>
              </div>

              {/* Right Column - Bank Details */}
              <div style={{
                width: '48%',
                padding: '0 0 0 0',
                textAlign: 'right',
                flexShrink: 0,
                marginLeft: 'auto'
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>
                  <b>Pay To: </b>
                </p>
                <div style={{ fontSize: '11px', marginBottom: '0', textAlign: 'right' }}>
                  Bank Name: {invoiceData?.bank_name || 'N/A'}<br />
                  Account Name: {invoiceData?.account_name || 'N/A'}<br />
                  Account Number: {invoiceData?.account_number || 'N/A'}<br />
                  IFSC: {invoiceData?.ifsc || 'N/A'}
                </div>
              </div>
            </div>

            {/* Bottom Row - Received By and Authorized By (Side by Side) */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              flexWrap: 'nowrap',
              marginTop: '5px',
              // Keep signature labels/lines above the fixed bottom footer line
              marginBottom: '60px'
            }}>
              {/* Left - Received By */}
              <div style={{
                width: '48%',
                padding: '0 10px 0 0',
                textAlign: 'left',
                flexShrink: 0
              }}>
                <div style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '14px', marginTop: '0', marginBottom: '2px', paddingBottom: '0' }}>
                  Received By
                </div>
                <hr style={{ margin: '0' }} />
              </div>

              {/* Right - Authorized By */}
              <div style={{
                width: '48%',
                padding: '0 0 0 0',
                textAlign: 'right',
                flexShrink: 0,
                marginLeft: 'auto'
              }}>
                <div style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px', marginTop: '0', marginBottom: '2px', paddingBottom: '0' }}>
                  Authorized By
                </div>
                <hr style={{ margin: '0' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer-2 - Fixed at very bottom */}
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          textAlign: 'center',
          padding: '8px 20px',
          borderTop: '1px solid #000',
          marginTop: '0',
          marginBottom: '0'
        }}>
          <p style={{ marginBottom: '15', fontSize: '14px', lineHeight: '1.2' }}>
            Powered by SOFTOMATION | Contact: +91-9999782899
          </p>
        </div>

        <div className="print-page-number" />

      </div>
    </Fragment>
  );
});

const SaleInvoice = ({ data, autoShow = false, documentType = "sale", onClose, autoPrintOnce = false }) => {
  const componentRef = useRef();
  const hasAutoPrinted = useRef(false);
  const [showPdf, setShowPDF] = useState(autoShow);
  const [sortAlphabetically, setSortAlphabetically] = useState(true);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `${data?.prefix || ""}${data?.invoice_number || "sale-invoice"}`,
  });

  const handleDownloadPdf = async () => {
    const el = componentRef.current;
    if (!el) return;

    // Render the current invoice DOM to a real PDF file
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: 1000,
      width: 1000,
      onclone: (clonedDoc) => {
        // Don't bake the on-page meta row into the PDF image; we draw it separately on pages 2+
        clonedDoc.querySelectorAll(".invoice-top-meta").forEach((n) => {
          n.style.display = "none";
        });
        const wrap = clonedDoc.querySelector(".wrapper");
        if (wrap) {
          wrap.style.width = "1000px";
          wrap.style.maxWidth = "1000px";
          wrap.style.margin = "0 auto";
        }
      },
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Set margins matching the print preview (e.g., ~10-20pt)
    const marginX = 20;
    const marginY = 20;

    const imgWidth = pageWidth - marginX * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = marginY;

    pdf.addImage(imgData, "PNG", marginX, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - marginY * 2;

    while (heightLeft > 0) {
      position = position - pageHeight;
      pdf.addPage();
      // Ensure the top margin is respected on subsequent pages
      pdf.addImage(imgData, "PNG", marginX, position + marginY, imgWidth, imgHeight);
      heightLeft -= pageHeight - marginY * 2;
    }

    const totalPages = pdf.getNumberOfPages();

    // Add header on pages 2..N (NOT on first page)
    const headerLeft = `${(() => {
      switch (documentType) {
        case "quotation":
          return "Quotation No";
        case "challan":
          return "Challan No";
        default:
          return "Invoice No";
      }
    })()}: ${data?.prefix || ""}${data?.invoice_number || ""}`;
    const headerRight = `${(() => {
      switch (documentType) {
        case "quotation":
          return "Quotation Date";
        case "challan":
          return "Challan Date";
        default:
          return "Invoice Date";
      }
    })()}: ${moment(data?.date).format("DD-MMM-YYYY")}`;

    // Draw header/page numbers as an overlay pass so it never gets covered by the image.
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    const headerY = 36; // comfortably inside visible area
    const headerBgHeight = 52;
    const pageNoY = pageHeight - 14;
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);

      // Page number: "Page X of Y"
      pdf.text(`Page ${p} of ${totalPages}`, pageWidth - marginX, pageNoY, { align: "right" });

      if (p === 1) continue;

      // Header (pages 2+ only): white strip + left/right text
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, headerBgHeight, "F");
      pdf.text(headerLeft, marginX, headerY);
      pdf.text(headerRight, pageWidth - marginX, headerY, { align: "right" });
    }

    const fileName = `${data?.prefix || ""}${data?.invoice_number || "sale-invoice"}.pdf`;
    pdf.save(fileName);
  };

  const [invoiceData, setInvoiceData] = useState(null);
  useEffect(() => {
    getSetting()
      .then((data) => {
        console.log("Invoice Settings fetched:", data?.result);
        setInvoiceData(data?.result);
      })
      .catch((error) => {
        console.error("Error fetching invoice settings:", error);
      });
  }, []);

  // Auto-show PDF on mobile (or when opened from list modal)
  useEffect(() => {
    if (autoShow && invoiceData) {
      setShowPDF(true);
    }
  }, [autoShow, invoiceData]);

  const showPrint = () => {
    setShowPDF(true);
  };

  const hideShowPrint = () => {
    setShowPDF(false);
    onClose?.();
  };

  // When opened for "Print PDF" from list: trigger print once after content is ready
  useEffect(() => {
    if (!autoPrintOnce || !showPdf || !data || !invoiceData || hasAutoPrinted.current) return;
    hasAutoPrinted.current = true;
    const t = setTimeout(() => {
      handlePrint();
      hideShowPrint();
    }, 400);
    return () => clearTimeout(t);
  }, [autoPrintOnce, showPdf, data, invoiceData]);

  useEffect(() => {
    if (!showPdf) return;
    const onEsc = (e) => {
      if (e.key === "Escape") hideShowPrint();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [showPdf]);

  // If autoPrintOnce is true, we visually hide the modal so the user doesn't see it flash
  const popupStyle = autoPrintOnce ? { opacity: 0, pointerEvents: 'none' } : {};

  return (
    <div>
      {showPdf && (
        <div className="popUp" style={popupStyle}>
          <div className="invoice-preview">
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 8, gap: 8 }}>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={hideShowPrint}
                aria-label="Close"
                style={{ marginLeft: "auto", color: "#ff4d4f" }}
              >
                Close
              </Button>
            </div>
            {/* Mobile Back Button */}
            <div className="d-lg-none mb-3">
              <Button
                type="default"
                onClick={() => hideShowPrint()}
                style={{ marginBottom: '16px' }}
              >
                ← Back to Details
              </Button>
            </div>

            <PrintToPdf
              ref={componentRef}
              data={data}
              invoiceData={invoiceData}
              documentType={documentType}
              sortAlphabetically={sortAlphabetically}
            />
            {invoiceData && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
                <Checkbox
                  checked={sortAlphabetically}
                  onChange={(e) => setSortAlphabetically(e.target.checked)}
                >
                  Sort Titles A-Z
                </Checkbox>
                <Button
                  type="primary"
                  shape="round"
                  onClick={() => {
                    handlePrint();
                    hideShowPrint();
                  }}
                >
                  Print PDF
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {!showPdf && invoiceData && !onClose && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button type="primary" shape="round" onClick={showPrint}>
            Preview
          </Button>
          <Button type="default" shape="round" onClick={handleDownloadPdf}>
            Download PDF
          </Button>
          <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', zIndex: -1000, visibility: 'hidden' }}>
            <PrintToPdf
              ref={componentRef}
              data={data}
              invoiceData={invoiceData}
              documentType={documentType}
              sortAlphabetically={sortAlphabetically}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleInvoice;
